import { FilterParam } from '../types';
import buildBaseEndpoint from './buildBaseEndpoint';

export function getDownloadColumnsMap() {
  const keyValue: [string, string][] = [
    // disease (col_sel must match API response keys so backend can fill TSV)
    ['numVariantsAssociatedToDisease', 'numVariantsAssociatedToDisease'],
    ['numGenesAssociatedToDisease', 'numGenesAssociatedToDisease'],
    ['disease_prevalence_class', 'disease_prevalence_class'],
    ['disease_prevalence_type', 'disease_prevalence_type'],
    ['disease_prevalence_geo_area', 'disease_prevalence_geo_area'],
    ['disease_inheritance', 'disease_inheritance'],
    ['numChemicals', 'numChemicals'],
    // drugs / chem/all (col_sel matches API response keys)
    ['chemID', 'chemID'],
    ['chemPrefName', 'chemPrefName'],
    ['chemMoleculeType', 'chemMoleculeType'],
    ['chemNumGDAs', 'chemNumGDAs'],
    ['chemNumPublicationsGDAs', 'chemNumPublicationsGDAs'],
    ['chemNumVDAs', 'chemNumVDAs'],
    ['chemNumPublicationsVDAs', 'chemNumPublicationsVDAs'],
    ['chemNumPublications', 'chemNumPublications'],
    // gda/summary
    ['chemIDs', 'chem_cuis'],
    ['diseaseUMLSCUI', 'diseaseUMLSCUI'],
    ['geneNcbiID', 'gene_ncbi'],
    ['geneDSI', 'DSI'],
    ['geneDPI', 'DPI'],
    ['genepLI', 'pli'],
    ['EL', 'EL'],
    ['EI', 'ei'],
    ['geneProteinClassNames', 'gp_class_names'],
    // gda/evidences
    ['associationType', 'association_type'],
    // vda/summary
    ['variantStrID', 'variant_name'],
    ['variantDSI', 'dsi'],
    ['variantDPI', 'dpi'],
    ['diseaseName', 'disease_name'],
    ['geneNcbiIDs', 'gene_ncbi'],
    ['gene_ensembl_id', 'gene_ensembl_id'],

    // vda/evidences
    ['diseaseType', 'disease_type'],
    ['geneSymbol', 'geneSymbol'],
    ['mostSevereConsequences', 'mostSevereConsequences'],
    ['sentenceHTML', 'sentence_html'], //'sentence_text'
    ['text', 'text'],
    ['clinical_significance', 'clinical_significance'],
    ['association', 'association'],
    ['citation_count', 'citation_count'],

    // disease class / disease/all (use same keys as API response for correct TSV export)
    ['diseaseClassName_MSH', 'diseaseClassName_MSH'],
    ['diseaseClassName_UMLS', 'diseaseClassName_UMLS'],
    ['diseaseClassName_DO', 'diseaseClassName_DO'],
    ['diseaseClassName_HPO', 'diseaseClassName_HPO'],
    // variants/all
    ['strID', 'strID'],
    ['DSI', 'DSI'],
    ['DPI', 'DPI'],
    ['DBSNPclass', 'DBSNPclass'],
    ['Alleles', 'alleles'],
    ['genome', 'genome'],
    ['exome', 'exome'],
    ['numDiseasesAssociatedToVariant', 'numDiseasesAssociatedToVariant'],
    ['numPublications', 'numPublications'],
    ['firstRef', 'firstRef'],
    ['lastRef', 'lastRef'],
    ['gene_ncbi_id', 'gene_ncbi_id'],
    ['gene_ensembl_id', 'gene_ensembl_id'],
    // gene/all (col_sel matches API response keys)
    ['symbol', 'symbol'],
    ['description', 'description'],
    ['proteins', 'protein'],
    ['PLI', 'pli'],
    ['ncbiID', 'gene_ncbi'],
    ['proteinIDs', 'proteinIDs'],
    ['proteinClassIDs', 'proteinClassIDs'],
    ['pathwayName', 'pathwayName'],
    ['pathwayID', 'pathwayID'],
    ['numDiseasesAssociatedToGene', 'num_diseases'],
    ['numVariantsAssociatedToGene', 'numVariantsAssociatedToGene'],
    ['ncbi_type', 'ncbi_type'],
    ['gene_ensembl_id', 'gene_ensembl_id'],
    // gda/all
    ['geneProteinStrIDs', 'genep_str_ids'],
    //
    ['score', 'score'],
    ['numPMIDsupportingAssociation', 'num_pmid_assoc'],
    ['numberChemsIncludedInEvidence', 'numberChemsIncludedInEvidence'],
    ['numberPmidsWithChemsIncludedInEvidence', 'numberPmidsWithChemsIncludedInEvidence'],
    ['numberNCTSWithChemsIncludedInEvidence', 'numberNCTSWithChemsIncludedInEvidence'],
    ['numDBSNPsupportingAssociation', 'num_var_dis'],
    ['yearInitial', 'year_initial'],
    ['yearFinal', 'year_final'],
    ['ei', 'ei'],
    ['geneNcbiType', 'gene_type'],
    ['reference', 'reference'],
    ['reference_type', 'reference_type'],
    ['icite_apt', 'icite_apt'],
  ];

  return new Map(keyValue);
}

export function buildDownloadEndpoint({
  extension,
  columnVisibilityModel,
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  mappingContent,
}) {
  const colMap = getDownloadColumnsMap();

  const selectedColumns = Object.entries(columnVisibilityModel)
    .filter(([key, value]) => key !== 'evidenceTimeline' && value)
    .map(([key]) => colMap.get(key) || key);

  const col_sel = selectedColumns.join(',');

  let filterParam: FilterParam | null = null;
  switch (view) {
    case 'GENES':
      filterParam = tab === 'GENES' ? 'gene_ncbi_id_filter' : 'gene_ncbi_id';
      break;
    case 'DISEASES':
      filterParam = tab === 'DISEASES' ? 'disease_filter' : 'disease';
      break;
    case 'VARIANTS':
      filterParam = 'variant';
      break;
    case 'CHEMICALS':
      filterParam = tab === 'CHEMICALS' ? 'chem_ids_filter' : 'chem_ids_init';
      break;
  }

  // The backend idents value (disease_filter expects CUIs without UMLS_ prefix)
  const backendDiseaseVocabPrefix = filterParam === 'disease' ? 'UMLS_' : '';
  const filterValue = searchItems.map(item => backendDiseaseVocabPrefix + item.id).join(',');

  const paramsMap = {
    source: source,
    ...(filterParam && { [filterParam]: filterValue }),
    col_sel: col_sel,
  };

  // Construct the endpoint URL (chem_ids_filter: repeat param per ID like buildResultEndpoint)
  const queryParams = new URLSearchParams();
  Object.entries(paramsMap).forEach(([param, value]) => {
    if (value === null || value === undefined || value === '') return;
    const strVal = String(value);
    if (param === 'chem_ids_filter' && strVal.includes(',')) {
      strVal.split(',').forEach(id => queryParams.append(param, id.trim()));
    } else {
      queryParams.append(param, strVal);
    }
  });

  const baseEndpoint = buildBaseEndpoint({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
  });

  let endPoint = `${baseEndpoint}/${extension}?${queryParams.toString().replace(/%2C/g, ',')}`;
  // override for chem downloads
  // the format for chem downloads is inconsitent with the other endpoints
  // 1 solution for the backend could be to remove "/all" from the url patterns
  // alternatively, nest tsv/excel under /all instead of as a sibling
  endPoint = endPoint.replace('chem/gda/all/', 'chem/gda/');

  return endPoint;
}
/*
random patterns are still missing -> tsv?source=ALL&disease=UMLS_C0028754&min_score=0.9950&max_score=1.0050&col_sel=variant_name,gene_symbol
&min_score=0.9950&max_score=1.0050&

/api/gda/summary/tsv?source=ALL&disease=UMLS_C0028754&col_sel=disease_name
/api/gda/summary/tsv?source=ALL&disease=UMLS_C0028754&col_sel=disease_name
*/

//Replace the function above for this one in TR-116
export function buildDownloadFiltersEndpoint({
  extension,
  columnVisibilityModel,
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  mappingContent,
  filters = {},
  sourceWeights = [],
}) {
  const colMap = getDownloadColumnsMap();

  const selectedColumns = Object.entries(columnVisibilityModel)
    .filter(([key, value]) => key !== 'evidenceTimeline' && value)
    .map(([key]) => colMap.get(key) || key);

  const col_sel = selectedColumns.join(',');

  let filterParam: FilterParam | null = null;
  switch (view) {
    case 'GENES':
      filterParam = tab === 'GENES' ? 'gene_ncbi_id_filter' : 'gene_ncbi_id';
      break;
    case 'DISEASES':
      filterParam = tab === 'DISEASES' ? 'disease_filter' : 'disease';
      break;
    case 'VARIANTS':
      filterParam = 'variant';
      break;
    case 'CHEMICALS':
      filterParam = tab === 'CHEMICALS' ? 'chem_ids_filter' : 'chem_ids_init';
      break;
  }

  // The backend idents value (disease_filter expects CUIs without UMLS_ prefix)
  const backendDiseaseVocabPrefix = filterParam === 'disease' ? 'UMLS_' : '';
  const filterValue = searchItems.map(item => backendDiseaseVocabPrefix + item.id).join(',');

  const paramsMap: Record<string, any> = {
    source: source,
    ...(filterParam && { [filterParam]: filterValue }),
    col_sel: col_sel,
    ...filters,
  };

  if (sourceWeights && sourceWeights.length > 0) {
    const weightsString = sourceWeights.map(sw => `${sw.name}:${sw.score}`).join(',');
    paramsMap.source_weights = weightsString;
  }

  // Construct the endpoint URL (chem_ids_filter: repeat param per ID)
  const queryParams = new URLSearchParams();
  Object.entries(paramsMap).forEach(([param, value]) => {
    if (value === null || value === undefined || value === '') return;
    const strVal = String(value);
    if (param === 'chem_ids_filter' && strVal.includes(',')) {
      strVal.split(',').forEach(id => queryParams.append(param, id.trim()));
    } else {
      queryParams.append(param, strVal);
    }
  });

  const baseEndpoint = buildBaseEndpoint({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
  });

  let endPoint = `${baseEndpoint}/${extension}?${queryParams.toString().replace(/%2C/g, ',').replace(/%3A/g, ':')}`;
  // override for chem downloads
  // the format for chem downloads is inconsitent with the other endpoints
  // 1 solution for the backend could be to remove "/all" from the url patterns
  // alternatively, nest tsv/excel under /all instead of as a sibling
  endPoint = endPoint.replace('chem/gda/all/', 'chem/gda/');

  return endPoint;
}
